/*
  Charlotte Cardin – bundle / add-on rules (front end).

  Products tagged "addon" can only be bought to complete a bundle
  (a product tagged "bundle"):
  - the "Complete your order" add-ons stay locked until a bundle is in the cart;
  - if the last bundle leaves the cart, every add-on is removed with it;
  - on an add-on's own page (direct link), adding to cart is locked too.

  The cart state comes from sections/cc-cart-status.liquid, which reads the
  product tags server side, and is refreshed after every cart change.
*/
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const root = document.documentElement;
const routesRoot = window.Shopify?.routes?.root || '/';
const strings = window.ccBundleStrings || {};
let enforcing = false;

const cartSectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);

function applyState(hasBundle) {
  root.dataset.ccHasBundle = hasBundle ? 'true' : 'false';
  document.querySelectorAll('[data-cc-requires-bundle]').forEach((button) => {
    button.disabled = !hasBundle || button.hasAttribute('data-cc-unavailable');
  });
  updateAddonPage(hasBundle);
}

/* Add-on opened from a direct link: lock its buy buttons until a bundle is in the cart. */
function updateAddonPage(hasBundle) {
  if (!document.body?.hasAttribute('data-cc-addon-page')) return;
  let note = document.querySelector('.cc-addon-page-note');
  if (!hasBundle && !note) {
    const anchor = document.querySelector('.buy-buttons-block');
    if (anchor) {
      note = document.createElement('p');
      note.className = 'cc-addon-page-note';
      note.textContent = strings.addonOnly || '';
      anchor.after(note);
    }
  }
  if (note) note.hidden = hasBundle;
}

async function fetchStatus() {
  const response = await fetch(`${routesRoot}?sections=cc-cart-status`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  const sections = await response.json();
  const html = sections['cc-cart-status'] || '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const data = doc.querySelector('[data-cc-cart-status]');
  return JSON.parse(data ? data.textContent : '{"hasBundle":false,"addonKeys":[]}');
}

function notify(message, action) {
  if (!message) return;
  let toast = document.querySelector('.cc-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'cc-toast';
    toast.setAttribute('role', 'status');
    document.body.append(toast);
  }
  toast.textContent = '';
  const text = document.createElement('span');
  text.textContent = message;
  toast.append(text);
  if (action) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cc-toast__action';
    button.textContent = action.label;
    button.addEventListener('click', () => {
      toast.classList.remove('is-visible');
      action.onClick();
    });
    toast.append(button);
  }
  toast.classList.add('is-visible');
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => toast.classList.remove('is-visible'), action ? 8000 : 5000);
}

/* Bundle pages: after adding the bundle, glide down to "Complete your order"
   instead of opening the cart drawer (setting in the add-ons section). */
const scrollTarget = () => document.querySelector('[data-cc-scroll-on-add="true"]');
let addingAddon = false;

if (scrollTarget()) {
  document.querySelectorAll('cart-drawer-component[auto-open]').forEach((drawer) => drawer.removeAttribute('auto-open'));
}

function openCart() {
  const trigger = document.querySelector('.cc-header__cart, .header-actions__cart-icon button, [ref="cartDrawerButton"]');
  if (trigger instanceof HTMLElement) trigger.click();
  else window.location.href = `${routesRoot}cart`;
}

function revealAddons() {
  const target = scrollTarget();
  if (!target) return;
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  target.classList.remove('is-highlighted');
  void target.offsetWidth;
  target.classList.add('is-highlighted');
  notify(strings.bundleAdded, { label: strings.viewCart, onClick: openCart });
}

/* Read the cart state, update the UI, and drop orphaned add-ons. */
async function enforce() {
  if (enforcing) return;
  enforcing = true;
  try {
    const status = await fetchStatus();
    applyState(status.hasBundle);

    if (!status.hasBundle && status.addonKeys.length) {
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'remove',
          context: 'cart',
          lines: status.addonKeys.map((id) => ({ id, quantity: 0 })),
          promise: deferred.promise,
        })
      );

      const updates = Object.fromEntries(status.addonKeys.map((key) => [key, 0]));
      const ids = cartSectionIds();
      const response = await fetch(`${routesRoot}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: ids.join(',') }),
      });
      const cart = await response.json();

      if (cart.status) {
        deferred.reject(new Error(cart.description || 'Cart update failed'));
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections || undefined,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cc-bundles',
          didError: false,
        },
      });

      notify(strings.removed);
    }
  } catch (error) {
    console.warn('[cc-bundles]', error);
  } finally {
    enforcing = false;
  }
}

/* "Complete your order": add an add-on through the theme's cart events,
   so the cart drawer and cart count update like a normal add to cart. */
async function addAddon(form) {
  if (root.dataset.ccHasBundle !== 'true') return;
  const button = form.querySelector('button[type="submit"]');
  const label = button?.textContent;
  const formData = new FormData(form);
  const ids = cartSectionIds();
  if (ids.length) formData.append('sections', ids.join(','));

  if (button) button.disabled = true;

  addingAddon = true;
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(`${routesRoot}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const added = await response.json();
    const cart = await (await fetch(`${routesRoot}cart.js`, { cache: 'no-store' })).json();

    if (added.status) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'cc-bundles', itemCount: 0 },
      });
      notify(added.description || strings.error);
      return;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: added.sections || undefined,
        items: cart.items,
        itemCount: 1,
        source: 'cc-bundles',
        didError: false,
      },
    });

    if (button) {
      button.textContent = strings.added || label;
      setTimeout(() => {
        button.textContent = label;
      }, 2000);
    }
  } catch (error) {
    deferred.reject(error);
    notify(strings.error);
  } finally {
    if (button) button.disabled = root.dataset.ccHasBundle !== 'true';
    setTimeout(() => {
      addingAddon = false;
    }, 0);
  }
}

/* Add-on page without a bundle in the cart: block the theme's own add to cart
   (capture phase, before the product form handles the submit). */
document.addEventListener(
  'submit',
  (event) => {
    if (!document.body?.hasAttribute('data-cc-addon-page')) return;
    if (root.dataset.ccHasBundle === 'true') return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest('[data-cc-addon-form]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    notify(strings.addonOnly);
  },
  true
);

document.addEventListener('submit', (event) => {
  const form = event.target instanceof Element ? event.target.closest('[data-cc-addon-form]') : null;
  if (!form) return;
  event.preventDefault();
  addAddon(form);
});

/* Every cart change (add, quantity, remove, from any component). */
document.addEventListener(StandardEvents?.cartLinesUpdate || 'shopify:cart:lines-update', (event) => {
  const done = event.promise && typeof event.promise.then === 'function' ? event.promise : Promise.resolve();
  const action = event.action ?? event.detail?.action;
  const isBundleAdd = action === 'add' && !addingAddon;
  done
    .then(async (result) => {
      await enforce();
      if (isBundleAdd && !result?.detail?.didError && root.dataset.ccHasBundle === 'true') revealAddons();
    })
    .catch(enforce);
});

/* Back/forward cache: the page may show an old cart. */
window.addEventListener('pageshow', (event) => {
  if (event.persisted) enforce();
});

/* First load: apply the state rendered by Liquid, then clean up if needed
   (e.g. the bundle was removed in another tab). */
try {
  const initial = JSON.parse(document.getElementById('CcBundleState')?.textContent || '{}');
  applyState(Boolean(initial.hasBundle));
  if (!initial.hasBundle && initial.addonKeys?.length) enforce();
} catch (error) {
  enforce();
}
